<script>
import { Bar } from 'vue-chartjs';

export default {
  name: 'SilosInOutBarChart',
  extends: Bar,
  props: {
    chartLabels: {
      type: Array,
      required: true
    },
    entriesData: {
      type: Array,
      required: true
    },
    exitsData: {
      type: Array,
      required: true
    },
    entriesSacasData: {
      type: Array,
      required: false,
      default: () => []
    },
    exitsSacasData: {
      type: Array,
      required: false,
      default: () => []
    },
    realEntriesKg: {
      type: Array,
      required: false,
      default: () => []
    },
    realExitsKg: {
      type: Array,
      required: false,
      default: () => []
    },
    realEntriesSacas: {
      type: Array,
      required: false,
      default: () => []
    },
    realExitsSacas: {
      type: Array,
      required: false,
      default: () => []
    },
    netEntriesLevel: {
      type: Array,
      required: false,
      default: () => []
    },
    netExitsLevel: {
      type: Array,
      required: false,
      default: () => []
    },
    netEntriesKg: {
      type: Array,
      required: false,
      default: () => []
    },
    netExitsKg: {
      type: Array,
      required: false,
      default: () => []
    },
    unit: {
      type: String,
      default: 'kg'
    },
    tooltipLabels: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data() {
    const unitLabel = this.unit || 'kg';
    return {
      chartDataConfig: {
        labels: this.chartLabels,
        datasets: [
          {
            label: `Entradas (${unitLabel})`,
            data: this.entriesData,
            backgroundColor: 'rgba(137, 194, 91, 0.8)',
            borderColor: 'rgba(137, 194, 91, 1)',
            borderWidth: 2,
            barPercentage: 0.75,
          },
          {
            label: `Saídas (${unitLabel})`,
            data: this.exitsData,
            backgroundColor: 'rgba(0, 100, 0, 0.8)',
            borderColor: 'rgba(0, 100, 0, 1)',
            borderWidth: 2,
            barPercentage: 0.75,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              min: 0,
              callback: (value) => {
                const n = Number(value);
                if (!Number.isFinite(n)) return value;
                // Formata com separador de milhar para facilitar leitura
                return n.toLocaleString('pt-BR');
              }
            }
          }],
          xAxes: [{
            ticks: {
              // Rótulos curtos (dd/MM): sem rotação forçada, o Chart.js
              // gira automaticamente só se realmente faltar espaço.
              maxRotation: 45,
              minRotation: 0,
              autoSkip: true
            }
          }]
        },
        layout: {
          padding: {
            bottom: 8
          }
        },
        tooltips: {
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems && tooltipItems.length ? tooltipItems[0].index : -1;
              if (Array.isArray(this.tooltipLabels) && this.tooltipLabels.length > index && index >= 0) {
                return this.tooltipLabels[index];
              }
              return tooltipItems && tooltipItems.length ? tooltipItems[0].xLabel : '';
            },
            label: (tooltipItem) => {
              const datasetIndex = tooltipItem.datasetIndex;
              const index = tooltipItem.index;
              const sentido = datasetIndex === 0 ? 'Entradas' : 'Saídas';
              const value = tooltipItem.yLabel;
              const fmt0 = (v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
              const fmt2 = (v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

              let text = `${sentido}: ${fmt2(value)} ${unitLabel}`;

              const kgArray = datasetIndex === 0 ? this.realEntriesKg : this.realExitsKg;
              if (Array.isArray(kgArray) && kgArray.length > index) {
                const kgValue = Number(kgArray[index]);
                if (Number.isFinite(kgValue) && kgValue !== 0) {
                  // Quando unit = 'kg', kgArray pode ser nível agregado; ajustamos o rótulo
                  const label = unitLabel === 'kg' ? 'nível agregado' : 'kg';
                  text += unitLabel === 'kg'
                    ? ` | ${fmt2(kgValue)} ${label}`
                    : ` | ${fmt0(kgValue)} ${label}`;
                }
              }

              const sacasRealArray = datasetIndex === 0 ? this.realEntriesSacas : this.realExitsSacas;
              const sacasArray = datasetIndex === 0 ? this.entriesSacasData : this.exitsSacasData;
              let sacasAdded = false;
              if (Array.isArray(sacasRealArray) && sacasRealArray.length > index) {
                const sacasReal = Number(sacasRealArray[index]);
                if (Number.isFinite(sacasReal) && sacasReal !== 0) {
                  text += ` (${fmt0(sacasReal)} sacas)`;
                  sacasAdded = true;
                }
              }
              if (!sacasAdded && Array.isArray(sacasArray) && sacasArray.length > index) {
                const sacasValue = Number(sacasArray[index]);
                if (Number.isFinite(sacasValue) && sacasValue !== 0) {
                  text += ` (${fmt0(sacasValue)} sacas)`;
                }
              }

              return text;
            }
          }
        },
        legend: {
          display: true,
          position: 'top'
        }
      }
    };
  },
  watch: {
    chartLabels: 'scheduleRender',
    entriesData: 'scheduleRender',
    exitsData: 'scheduleRender'
  },
  mounted() {
    // Render no próximo tick para garantir que o container já está com o
    // tamanho final no DOM (evita canvas com largura/altura zero quando
    // a página é re-montada após navegar de volta).
    this.$nextTick(() => this.renderChartFromProps());
  },
  beforeDestroy() {
    if (this.$data._chart) {
      this.$data._chart.destroy();
      this.$data._chart = null;
    }
  },
  methods: {
    scheduleRender() {
      this.$nextTick(() => this.renderChartFromProps());
    },
    renderChartFromProps() {
      this.chartDataConfig = {
        labels: this.chartLabels,
        datasets: [
          {
            label: `Entradas (${this.unit})`,
            data: this.entriesData || [],
            backgroundColor: 'rgba(137, 194, 91, 0.8)',
            borderColor: 'rgba(137, 194, 91, 1)',
            borderWidth: 2,
            barPercentage: 0.75,
          },
          {
            label: `Saídas (${this.unit})`,
            data: this.exitsData || [],
            backgroundColor: 'rgba(0, 100, 0, 0.8)',
            borderColor: 'rgba(0, 100, 0, 1)',
            borderWidth: 2,
            barPercentage: 0.75,
          }
        ]
      };
      if (this.$data._chart) {
        this.$data._chart.destroy();
        this.$data._chart = null;
      }
      this.renderChart(this.chartDataConfig, this.options);
    }
  }
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
}
</style>
